import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { createEventContactsApi } from '../lib/eventContactsApi';
import { errorText } from '../lib/apiErrors';

export const CONTACT_EVENTS_PAGE_SIZE = 20;
// The route's own `limit` ceiling: a reload that must cover more rows than this reads them in several pages.
const MAX_PAGE_SIZE = 100;

const emptyState = (key) => ({
    key, rows: [], total: 0, truncated: false, nextCursor: null,
    loading: Boolean(key), loadingMore: false, error: null, loadMoreError: null,
});

// A reply that is not the `{events, next_cursor, truncated, total}` object (a bare list, nothing) reads as empty.
function readPage(body) {
    const page = body && typeof body === 'object' && !Array.isArray(body) ? body : {};
    const rows = Array.isArray(page.events) ? page.events : [];
    return {
        rows,
        total: Number.isFinite(page.total) ? page.total : rows.length,
        truncated: Boolean(page.truncated),
        nextCursor: page.next_cursor || null,
    };
}

const rowKey = (row) => row?.attendee_id || row?.event?.event_id;

// The status stays so a caller can tell a hidden contact (404) from a failed read.
const toError = (err) => ({ status: err?.response?.status ?? null, message: errorText(err, 'Could not load this contact\'s events') });

/**
 * One contact's events (#889), read once from `GET /api/contacts/{id}/events` at `CONTACT_EVENTS_PAGE_SIZE`
 * a page. Contact Detail's count strip, rail card and Events tab share this one read, so the page makes no
 * second request for them.
 *
 * Returns `{rows, total, truncated, nextCursor, loading, loadingMore, error, loadMoreError, loadMore, reload}`:
 * - `rows` are the loaded pages' wire rows; `total` counts every visible row (the server caps it at 500, and
 *   `truncated` says it did).
 * - `error` is the first page's failure, `{status, message}`: 404 means the contact is hidden or gone. A
 *   failed reload keeps the rows it had. `loadMoreError` is a failed `loadMore()`; the loaded rows stay.
 * - `reload()` re-reads the list after a write, down to as many rows as are loaded now (a user who paged to
 *   60 rows keeps 60, read in as few calls as the route's limit of 100 allows), never collapsing it to
 *   page 1; another contact id starts clean.
 */
export default function useContactEvents(contactId) {
    const { getAuthHeader } = useAuth();
    const authRef = useRef(getAuthHeader);
    useEffect(() => { authRef.current = getAuthHeader; });
    const api = useMemo(() => createEventContactsApi(() => (authRef.current ? authRef.current() : {})), []);
    const [reloads, setReloads] = useState(0);
    const [state, setState] = useState(() => emptyState(contactId));
    const stateRef = useRef(state);
    useEffect(() => { stateRef.current = state; });
    // Bumped by every page-1 read: a "load more" that started before it must not append to the new list.
    const generation = useRef(0);
    const moreInFlight = useRef(false);

    useEffect(() => {
        const mine = ++generation.current;
        // A reload of this contact keeps as many rows as the user has loaded; a new contact starts at one page.
        const loaded = stateRef.current.key === contactId ? stateRef.current.rows.length : 0;
        const want = Math.max(CONTACT_EVENTS_PAGE_SIZE, loaded);
        setState((prev) => (prev.key === contactId
            ? { ...prev, loading: Boolean(contactId), loadingMore: false, error: null, loadMoreError: null }
            : emptyState(contactId)));
        if (!contactId) return undefined;
        (async () => {
            try {
                // `want` rows come in ceil(want / 100) calls at most: bounded by the count asked for, not by what the server answers.
                let page = readPage(await api.listContactEvents(contactId, { limit: Math.min(want, MAX_PAGE_SIZE) }));
                let rows = page.rows;
                for (let call = 1; call < Math.ceil(want / MAX_PAGE_SIZE) && page.nextCursor && generation.current === mine; call += 1) {
                    const seen = new Set(rows.map(rowKey));
                    // eslint-disable-next-line no-await-in-loop
                    page = readPage(await api.listContactEvents(contactId, { limit: Math.min(want - rows.length, MAX_PAGE_SIZE), cursor: page.nextCursor }));
                    rows = [...rows, ...page.rows.filter((row) => !seen.has(rowKey(row)))];
                }
                if (generation.current === mine) setState({ ...emptyState(contactId), ...page, rows, loading: false });
            } catch (err) {
                if (generation.current === mine) setState((prev) => ({ ...prev, loading: false, error: toError(err) }));
            }
        })();
        return () => { generation.current += 1; };
    }, [api, contactId, reloads]);

    const loadMore = useCallback(async () => {
        const current = stateRef.current;
        if (current.key !== contactId || !current.nextCursor || moreInFlight.current) return;
        const mine = generation.current;
        moreInFlight.current = true;
        setState((prev) => ({ ...prev, loadingMore: true, loadMoreError: null }));
        try {
            const page = readPage(await api.listContactEvents(contactId, { limit: CONTACT_EVENTS_PAGE_SIZE, cursor: current.nextCursor }));
            if (generation.current !== mine) return;
            setState((prev) => {
                const seen = new Set(prev.rows.map(rowKey));
                return {
                    ...prev,
                    rows: [...prev.rows, ...page.rows.filter((row) => !seen.has(rowKey(row)))],
                    total: page.total, truncated: page.truncated, nextCursor: page.nextCursor, loadingMore: false,
                };
            });
        } catch (err) {
            if (generation.current === mine) setState((prev) => ({ ...prev, loadingMore: false, loadMoreError: toError(err) }));
        } finally {
            moreInFlight.current = false;
        }
    }, [api, contactId]);

    const reload = useCallback(() => setReloads((count) => count + 1), []);

    const current = state.key === contactId ? state : emptyState(contactId);
    return {
        rows: current.rows, total: current.total, truncated: current.truncated, nextCursor: current.nextCursor,
        loading: current.loading, loadingMore: current.loadingMore, error: current.error,
        loadMoreError: current.loadMoreError, loadMore, reload,
    };
}
